import { useContext, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import CartContext from "../../context/cart";
import UserContext from "../../context/user";
import { getAnswerValue } from "../../utils/partial";
import AccordionBox from "../accordion-box";
import { QUESTION_MAPPING } from "../../utils/cart";
dayjs.extend(LocalizedFormat);
dayjs.extend(localeData);

const BoxWrapper = ({ readOnly, isConfirmPhase, tempCartToSubmit, tempBookingDate }) => {
  const { callApi } = useContext(UserContext);
  const { setCurrentSectionIndex, labels, survey, appLang, cartRequest } = useContext(CartContext);
  const questions = Object.entries(
    QUESTION_MAPPING(
      cartRequest?.deliveryOption === "INDIRIZZO",
      !!cartRequest?.previousReservationStart && !isConfirmPhase
    )
  );
  const data = useMemo(() => {
    function getFieldAnswerValue(value) {
      if (value.requestField === "reservationStart" && tempBookingDate?.dateFrom) {
        return {
          answerValue: tempBookingDate.dateFrom.valueOf(),
        };
      }
      if (value.behaviour === "distribution-point" && tempBookingDate?.pickUpPointId) {
        return {
          answerValue: tempBookingDate.pickUpPointId,
        };
      }

      if (value.requestField) {
        return {
          answerValue:
            cartRequest?.deliveryOption === "INDIRIZZO"
              ? cartRequest?.deliveryInformation?.[value.requestField]
              : Number(cartRequest?.[value.requestField]),
        };
      }
      const questionId = survey?.definition?.sections?.[value.section]?.questions?.[value.question];
      const formAnswerValue = tempCartToSubmit
        ? tempCartToSubmit?.values?.[questionId.id] ?? null
        : null;
      if (formAnswerValue) {
        return {
          answerValue: formAnswerValue,
        };
      }

      return getAnswerValue(
        survey,
        cartRequest?.surveySubmission?.answers,
        value.section,
        value.question
      );
    }
    return questions?.map(([key, value]) => {
      const answerValue = getFieldAnswerValue(value);

      const formattedAnswerValue =
        value.answerType === "date" && answerValue?.answerValue
          ? dayjs(answerValue?.answerValue).locale(appLang?.value).format(value.format)
          : answerValue?.[value.answerType];

      return [
        key,
        {
          value: formattedAnswerValue,
          behaviour: value.behaviour,
          type: value.answerType,
          box: value.box,
        },
      ];
    });
  }, [questions, tempCartToSubmit, survey, cartRequest, appLang?.value, tempBookingDate]);

  const pickUpPointId = data?.find((item) => item[0] === "at")?.[1]?.value;
  const {
    isFetching,
    error,
    data: pickUpPointData,
  } = useQuery(
    ["pickup-point", pickUpPointId],
    () => callApi(`/cart/pickup-point/${pickUpPointId}`),
    {
      enabled: !!pickUpPointId && cartRequest?.deliveryOption === "PUNTO_VENDITA",
    }
  );

  const onEdit = useCallback(
    (index) => {
      setCurrentSectionIndex(index);
    },
    [setCurrentSectionIndex]
  );

  const accordionBoxes = useMemo(
    () => [
      {
        title: labels?.WELFARE_CART_BOX_YOUR_DATA || "Your data",
        editLabel: labels?.WELFARE_BOX_CHANGE_DATA || "Change your data",
        id: "personal-data",
      },
      ...(cartRequest?.deliveryOption === "INDIRIZZO"
        ? [
            {
              title: labels?.WELFARE_CART_BOX_DELIVERY_ADDRESS || "Delivery address",
              editLabel: labels?.WELFARE_CART_BOX_CHANGE_ADDRESS || "Change address",
              id: "address",
            },
          ]
        : [
            {
              title: labels?.WELFARE_CART_BOX_DISTRIBUTION_POINT || "Punto di distribuzione",
              editLabel:
                labels?.WELFARE_BOX_CHANGE_DISTRIBUTION_POINT || "Change the distribution point",
              id: "pick-up-point",
            },
            {
              title: labels?.WELFARE_CART_BOX_PICKUP || "Pick-up appointment",
              editLabel: labels?.WELFARE_CART_BOX_CHANGE_APPOINTMENT || "Change appointment",
              id: "appointment",
            },
          ]),
    ],
    [labels, cartRequest]
  );

  return (
    <div className="box-wrapper box-wrapper--accordion">
      {accordionBoxes?.map((accordion, i) => (
        <AccordionBox
          title={accordion.title}
          onEdit={() => onEdit(i)}
          editLabel={accordion.editLabel}
          key={i}
          readOnly={readOnly}
          defaultOpen={true}
        >
          <span className="box-wrapper__info">
            {accordion.id === "appointment" && `${labels?.["WELFARE_CART_BOX_DATE"]}: `}
            {data
              ?.filter(([_key, value]) => value.box === i && value.behaviour === "value-only")
              ?.map(([_key, value]) => value.value)
              ?.join(" ")}
          </span>
          {data
            ?.filter(
              ([_key, value]) =>
                value.box === i && !["value-only", "distribution-point"].includes(value.behaviour)
            )
            ?.map(
              ([key, value]) =>
                value.value && (
                  <div
                    className="box-wrapper__info"
                    key={key}
                    data-label={`WELFARE_CART_BOX_${key.toUpperCase()}`}
                    translate="no"
                  >
                    {labels?.[`WELFARE_CART_BOX_${key.toUpperCase()}`]}:{" "}
                    <span>{value.value || "-"}</span>
                  </div>
                )
            )}
          {data
            ?.filter(([_key, value]) => value.box === i && value.behaviour === "distribution-point")
            ?.map(([key, value]) =>
              isFetching ? (
                <span className="loading-spinner" key={key} />
              ) : error ? (
                <div className="error" key={key}>
                  Error retrieving pickup point information
                </div>
              ) : (
                value.value && (
                  <span
                    className="box-wrapper__info"
                    key={key}
                    data-label={`WELFARE_CART_BOX_${key.toUpperCase()}`}
                  >
                    {labels?.[`WELFARE_CART_BOX_${key.toUpperCase()}`]}:{" "}
                    <strong>{pickUpPointData?.name}</strong> - {pickUpPointData?.address}
                  </span>
                )
              )
            )}
          {data
            ?.filter(([_key, value]) => value.box === i && value.behaviour === "appointment")
            ?.map(
              ([key, value]) =>
                value.value && (
                  <div
                    className="box-wrapper__info"
                    key={key}
                    data-label={`WELFARE_CART_BOX_${key.toUpperCase()}`}
                  >
                    {labels?.[`WELFARE_CART_BOX_${key.toUpperCase()}`]}:{" "}
                    <span>{value.value || "-"}</span>
                  </div>
                )
            )}
        </AccordionBox>
      ))}
    </div>
  );
};

export default BoxWrapper;
